import {Component, inject} from '@angular/core';
import {Router, RouterOutlet} from '@angular/router';
import {MatIconModule} from '@angular/material/icon';
import {MatMenuModule} from '@angular/material/menu';
import {MatDividerModule} from '@angular/material/divider';
import {BreakpointObserver} from '@angular/cdk/layout';
import {TranslocoPipe} from '@jsverse/transloco';
import {MemoryService} from '../../services/memory.service';
import {StorageService} from '../../services/storage.service';
import {RedirectService} from '../../services/redirect.service';
import {AppointmentSelectionService} from '../../../features/appointment-selection/appointment-selection.service';
import {PatientManagementService} from '../../../features/patient-management/patient-management.service';
import {IconButtonComponent} from '../../../shared/components/icon-button/icon-button.component';
import {environment} from '../../../../environments/environment';

/**
 * The app bar and footer around every page of the booking app.
 *
 * A layout route rather than part of the root component, so a page that must not show them simply
 * lives outside this route — see `app.routes.ts`. Pages reached from a link by someone with no
 * account (the waitlist) do that, since the account button would only lead somewhere they cannot go.
 */
@Component({
  selector: 'app-layout',
  imports: [RouterOutlet, IconButtonComponent, MatMenuModule, MatDividerModule, MatIconModule, TranslocoPipe],
  templateUrl: './app-layout.component.html',
  styleUrl: './app-layout.component.scss'
})
export class AppLayoutComponent {
  memoryService = inject(MemoryService);
  storageService = inject(StorageService);
  appointmentSelectionService = inject(AppointmentSelectionService);
  patientManagementService = inject(PatientManagementService);
  router = inject(Router);
  redirectService = inject(RedirectService);

  mode: 'mobile' | 'desktop' = 'desktop';

  constructor(private breakpointObserver: BreakpointObserver) {
    this.breakpointObserver.observe([
      '(max-width: 705px)',     // mobile
      '(min-width: 704px)'      // desktop
    ]).subscribe(result => {
      if (result.breakpoints['(max-width: 705px)']) {
        this.mode = 'mobile';
      } else if (result.breakpoints['(min-width: 704px)']) {
        this.mode = 'desktop';
      }
    });
  }

  profile() {
    this.router.navigate(['/profile']);
  }

  logout() {
    this.memoryService.refreshToken = undefined;
    this.memoryService.accessToken = undefined;
    this.memoryService.patientId = undefined;
    this.memoryService.patientEmail = undefined;
    this.memoryService.isPatientManager = undefined;
    this.storageService.removePatient();

    this.appointmentSelectionService.resetSelection();
    this.patientManagementService.reset();

    const url = this.router.url;
    const state = history.state;
    this.redirectService.set({url: url, state: state});

    this.router.navigate(['/login']);
  }

  login() {
    this.router.navigate(['/login']);
  }

  appointments() {
    this.router.navigate(['/profile/appointments']);
  }

  protected readonly environment = environment;
}
